// DelegationNotes — a delegation's handover notes, each labelled by its author,
// with an edit control for the viewer's OWN note (owner decision 2026-10-25).
//
// A delegation carries up to two notes: the EMPLOYEE's (the delegator) and,
// on cover a manager arranged, the MANAGER's. Each author edits only theirs —
// the server enforces it (PATCH /rbac/delegations/:id/notes); this only offers
// the control to the right person:
//   * employee (delegator) — sees both, edits theirs;
//   * manager who arranged it — sees and edits theirs (the employee's note is
//     between the two parties, so it is not sent to them);
//   * delegate — reads both. On self-cover (the manager arranged it and named
//     themselves, NE-17) the delegate is ALSO the arranger: they read the
//     employee's message as the cover and edit their own note (`alsoArranger`).
// Once the delegation is over (revoked / declined / rejected / expired / past
// its end) the notes are read-only and the field says why.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { MessageSquareText, Pencil } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { editDelegationNote, type Delegation } from '@/services/rbacService';
import { Button } from '@/features/shared/components/ui/button';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { HANDOVER_NOTE_MAX, firstName } from './handover';
import { HandoverNote, normaliseNote } from './HandoverNote';

export type NotesViewer = 'employee' | 'manager' | 'delegate';

const CLOSED = new Set(['revoked', 'rejected', 'declined', 'expired']);

/** First name, else the full name, else the fallback — never an id. */
const nameOr = (name: string | null | undefined, fallback: string) => firstName(name) || (name ?? '').trim() || fallback;

/** Is the delegation over, so its notes can no longer change? Mirrors auth
 *  noteEditClosedReason (which answers 409 regardless). */
export function notesClosed(d: Pick<Delegation, 'status' | 'revokedAt' | 'endsAt'>, now: Date = new Date()): boolean {
  if (d.revokedAt || CLOSED.has(String(d.status ?? ''))) return true;
  const end = new Date(d.endsAt).getTime();
  return !Number.isNaN(end) && end <= now.getTime();
}

/** Both notes for a view. An older auth sends only `note`: it is the
 *  employee's unless the cover was arranged (then the manager wrote it). */
export function delegationNotes(d: Delegation): { employee: string; manager: string } {
  if (d.employeeNote !== undefined || d.managerNote !== undefined) {
    return { employee: normaliseNote(d.employeeNote), manager: normaliseNote(d.managerNote) };
  }
  const single = normaliseNote(d.note);
  return d.arrangedByUserId ? { employee: '', manager: single } : { employee: single, manager: '' };
}

export interface DelegationNotesProps {
  delegation: Delegation;
  viewer: NotesViewer;
  /** Called with the saved delegation (the server's view for this editor). */
  onSaved?: (d: Delegation) => void;
  /** Shown in place of the viewer's own note while it is empty. */
  ownEmptyText?: string;
  /** viewer="delegate" only: they also arranged this cover (self-cover), and
   *  auth listed it in their arrangedByMe, so the manager note is theirs. */
  alsoArranger?: boolean;
  className?: string;
}

export const DelegationNotes: React.FC<DelegationNotesProps> = ({ delegation: d, viewer, onSaved, ownEmptyText, alsoArranger, className }) => {
  const notes = delegationNotes(d);
  const closed = notesClosed(d);
  const delegator = nameOr(d.delegatorName, 'Colleague');
  const delegate = nameOr(d.delegateName, 'your delegate');
  const manager = nameOr(d.managerNoteByName, 'their manager');

  const managerLabel = viewer === 'manager' ? `Your note to ${delegate}` : viewer === 'employee' ? `Note from ${manager} to ${delegate}` : `Note from ${manager}`;
  const employeeLabel = viewer === 'employee' ? `Your message to ${delegate}` : `Message from ${delegator}`;

  // Self-cover (NE-17): the employee's message read as the cover, plus their
  // own note. An unconfirmed author stays read-only (auth answers 409).
  if (viewer === 'delegate' && alsoArranger && !d.noteAuthorUnconfirmed) {
    return (
      <div className={`space-y-1.5 ${className ?? ''}`} data-testid="delegation-notes">
        <HandoverNote note={notes.employee} label={employeeLabel} />
        <OwnNote
          delegation={d}
          kind="manager"
          text={notes.manager}
          label="Your note"
          closed={closed}
          onSaved={onSaved}
          emptyText={ownEmptyText ?? "You haven't left a note."}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-1.5 ${className ?? ''}`} data-testid="delegation-notes">
      {viewer !== 'employee' && viewer !== 'manager' ? (
        <>
          <HandoverNote note={notes.manager} label={managerLabel} />
          <HandoverNote note={notes.employee} label={employeeLabel} />
        </>
      ) : (
        <>
          {/* The other author's note: read-only. The manager never receives the employee's. */}
          {viewer === 'employee' && <HandoverNote note={notes.manager} label={managerLabel} />}
          {viewer === 'manager' && d.noteAuthorUnconfirmed ? (
            // auth #42 round 2: a note saved before KaziPeople recorded who
            // wrote it may be the employee's, so auth sends the arranger none
            // of its text — and editing it is refused until it is attributed.
            <div className="rounded-[10px] border border-dashed border-border px-3 py-2" data-testid="own-note-unconfirmed">
              <div className="text-xs text-muted-foreground">{managerLabel}</div>
              <p className="mt-1 text-sm text-muted-foreground">
                A note exists, but who wrote it is still being confirmed. You'll be able to see and edit your note once it is.
              </p>
            </div>
          ) : (
          <OwnNote
            delegation={d}
            kind={viewer}
            text={viewer === 'employee' ? notes.employee : notes.manager}
            label={viewer === 'employee' ? employeeLabel : managerLabel}
            closed={closed}
            onSaved={onSaved}
            emptyText={ownEmptyText ?? (viewer === 'employee' ? `You haven't left ${delegate} a message.` : `You haven't left ${delegate} a note.`)}
          />
          )}
        </>
      )}
    </div>
  );
};

/** The viewer's OWN note — labelled as theirs even while empty, so "you haven't
 *  written one" never reads as "it's gone" (NE-9, HO-0mvpyl8ib001-53e48b). */
const OwnNoteText: React.FC<{ text: string; label: string; emptyText?: string }> = ({ text, label, emptyText }) =>
  text ? (
    <HandoverNote note={text} label={label} />
  ) : (
    <div className="rounded-[10px] border border-dashed border-border px-3 py-2" data-testid="own-note-empty">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MessageSquareText className="w-3.5 h-3.5" aria-hidden />
        {label}
      </div>
      {emptyText && <p className="mt-1 text-sm text-muted-foreground">{emptyText}</p>}
    </div>
  );

const OwnNote: React.FC<{
  delegation: Delegation;
  kind: 'employee' | 'manager';
  text: string;
  label: string;
  closed: boolean;
  onSaved?: (d: Delegation) => void;
  emptyText?: string;
}> = ({ delegation: d, kind, text: fromProps, label, closed, onSaved, emptyText }) => {
  const [editing, setEditing] = useState(false);
  // What the server last confirmed — shown at once after a save, before (or
  // without) the parent reloading.
  const [saved, setSaved] = useState<string | null>(null);
  const text = saved ?? fromProps;
  const [draft, setDraft] = useState(text);
  const [saving, setSaving] = useState(false);

  if (closed) {
    return (
      <div data-testid={`own-note-${kind}`}>
        {/* Over, but still say there was no note — an empty slot is not an answer. */}
        <OwnNoteText text={text} label={label} emptyText={emptyText} />
        <div className="mt-1 text-xs text-muted-foreground" data-testid="notes-closed">
          This delegation is over, so its notes can no longer be changed.
        </div>
      </div>
    );
  }
  if (!editing) {
    return (
      <div data-testid={`own-note-${kind}`}>
        <OwnNoteText text={text} label={label} emptyText={emptyText} />
        <Button
          variant="ghost"
          size="sm"
          className="mt-0.5 h-7 px-2 text-xs"
          onClick={() => {
            setDraft(text);
            setEditing(true);
          }}
        >
          <Pencil className="w-3 h-3 mr-1" aria-hidden />
          {text ? 'Edit your note' : 'Add a note'}
        </Button>
      </div>
    );
  }
  const save = async () => {
    setSaving(true);
    try {
      const next = normaliseNote(draft);
      const updated = await editDelegationNote(d.id, next || null);
      const own = updated ? delegationNotes(updated)[kind] : next;
      setSaved(own);
      toast.success(next ? 'Note saved' : 'Note removed');
      setEditing(false);
      onSaved?.(updated);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save your note'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-1.5" data-testid={`own-note-${kind}`}>
      <label className="text-xs text-muted-foreground" htmlFor={`note-${d.id}-${kind}`}>
        {label}
      </label>
      <Textarea
        id={`note-${d.id}-${kind}`}
        value={draft}
        maxLength={HANDOVER_NOTE_MAX}
        rows={3}
        onChange={(e) => setDraft(e.target.value.slice(0, HANDOVER_NOTE_MAX))}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={saving || normaliseNote(draft) === text} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save note'}
        </Button>
        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <span className="text-xs text-muted-foreground">
          {draft.length}/{HANDOVER_NOTE_MAX} · {kind === 'manager' ? 'Only your note changes.' : 'Only your message changes.'}
        </span>
      </div>
    </div>
  );
};

export default DelegationNotes;
